
import { API_URLS, } from "@bracket/platform/constants";
import * as Q from "./queries";
import type { ResultType, } from "./queries";
import * as G from "@globals";
import { EthereumAddress } from "@bracket/types";

/**
 * Execute a GQL query.
 * @param {keyof typeof Q} queryName - the name of the query to execute
 * @param {...(string | number)} args - the arguments to pass to the query
 * @returns {Promise<any>} - the result of the query
 */
export const gql = async <QT extends keyof ResultType>( queryName: QT, ...args: (string | number)[] ): Promise<Q.ResultType[QT]> => {

	try {

		const { address, } = G.wallet.getBracketWallet();

		if(!address)
			throw new Error("Vaults::gql requires a connected wallet.");

		if(queryName !== "MANAGED_VAULT") {
			const cached = getCachedData(queryName, address);
			if(cached) {
				if(cached instanceof Promise) {
					const response = await cached;
					const clone = response.clone();
					if(response.ok) {
						const { data } = await clone.json();
						return data;
					}
				}
				return cached;
			}
		}

		if(!address)
			throw new Error("Vaults::gql requires a connected wallet.");

		const { GRAPHQL_API, } = API_URLS;
		//@ts-ignore .. sure i can apply a spread operation....
		const query = Q[queryName](...args);

		const fetcher = fetch(GRAPHQL_API, {
			method: "POST",
			body: JSON.stringify({
				query,
			}),
			headers: {
				"Content-Type": "application/json",
				wallet: address,
			},
		});

		if(queryName !== "MANAGED_VAULT") {
			setFetching(fetcher, queryName, address);
		}

		const responding = await fetcher;
		const response = responding.clone();

		if(response.ok) {
			const { data } = await response.json();
			if(queryName !== "MANAGED_VAULT") {
				unsetFetching(queryName, address);
				setCache(queryName, address, args, data);
			}
			return data;
		}

		throw new Error("graphql query has failed.");

	}
	catch(err) {

		throw new Error(`Vaults::gql has failed - ${err instanceof Error ? err.message : "unknown error"}`);
	}

};


const cache: Partial<Record<`${keyof ResultType}-${EthereumAddress}`, { since: number, data: any }>> = {};
const fetching: Partial<Record<`${keyof ResultType}-${EthereumAddress}`, { fetcher: Promise<unknown>, }>> = {};

globalThis.cack = cache;
globalThis.feck = fetching;

const setCache = (queryName: keyof ResultType, address: EthereumAddress, args: (string | number)[], data: any) => {
	const key = `${queryName}-${address}`;
	cache[key] = {
		since: Date.now(),
		data,
	}
}

const setFetching = ( fetcher: Promise<unknown>, queryName: keyof ResultType, address: EthereumAddress, ) => {
	const key = `${queryName}-${address}`;
	fetching[key] = {
		fetcher,
	}
}

const unsetFetching = ( queryName: keyof ResultType, address: EthereumAddress, ) => {
	const key = `${queryName}-${address}`;
	delete fetching[key];
}

const getCachedData = ( queryName: keyof ResultType, address: EthereumAddress, ) => {

	const key = `${queryName}-${address}`;
	if(fetching[key]) {
		return fetching[key].fetcher;
	}
	const cached = cache[key];
	if(cached && Date.now() - cached.since < QueryTTL[queryName]) {

		return cached.data;
	}
	return null;
}

export const purgeCache = ( queryName: keyof ResultType, address: EthereumAddress, ...args: (string | number)[] ): void => {
	const key = `${queryName}-${address}`;
	const cached = cache[key];
	if(cached) {
		cached.since = 0;
	}
}

const QueryTTL: Record<keyof ResultType, number> = {
	VAULTS: 1000 * 60 * 60 * 24, // 24 hours
	VAULT_DATA: 1000 * 60, // 1 minte
	BRKT_PRICE_PERIOD: 1000 * 60, // 1 minute
	MANAGED_VAULT: 0, // 1 minte
	MANAGED_VAULT_DATA: 1000 * 60, // 1 minute
	PENNYWORKS_VAULT_POSITIONS: 1000 * 60, // 1 minute
}
